import React, { useState } from 'react';
import { Create, useForm } from '@refinedev/antd';
import { Form, Input, Select, Button, Space, Card, InputNumber, Alert, Divider } from 'antd';
import {
  PlusOutlined,
  MinusCircleOutlined,
  InfoCircleOutlined,
} from '@ant-design/icons';

const { Option } = Select;

export const SandboxCreate: React.FC = () => {
  const { formProps, saveButtonProps, onFinish } = useForm({
    redirect: 'show',
  });
  const [submitting, setSubmitting] = useState(false);

  // Transform form data before submission to match API contract
  const handleFinish = async (values: unknown) => {
    setSubmitting(true);
    try {
      // Transform labels from array to object
      const formValues = values as {
        name: string;
        cluster: string;
        labels?: Array<{ key: string; value: string }>;
        forks: unknown[];
        ttl_seconds?: number;
      };

      const labels: Record<string, string> = {};
      if (formValues.labels) {
        formValues.labels.forEach((label) => {
          labels[label.key] = label.value;
        });
      }

      const sandboxData = {
        name: formValues.name,
        cluster: formValues.cluster,
        labels,
        forks: formValues.forks,
        ttl_seconds: formValues.ttl_seconds,
      };

      await onFinish?.(sandboxData);
    } finally {
      setSubmitting(false);
    }
  };

  // Validation rules
  const validateSandboxName = (_: unknown, value: string) => {
    if (!value) {
      return Promise.reject(new Error('Sandbox name is required'));
    }
    // Lowercase alphanumeric with hyphens
    if (!/^[a-z0-9-]+$/.test(value)) {
      return Promise.reject(
        new Error('Name must be lowercase alphanumeric with hyphens only')
      );
    }
    if (value.length > 63) {
      return Promise.reject(new Error('Name must be 63 characters or less'));
    }
    return Promise.resolve();
  };

  const validateImageURL = (_: unknown, value: string) => {
    if (!value) {
      return Promise.reject(new Error('Image URL is required'));
    }
    // Basic format check for container image
    if (!/^[a-z0-9-_./]+:[a-z0-9-_.]+$/i.test(value) && !/^[a-z0-9-_./]+@sha256:[a-f0-9]+$/i.test(value)) {
      return Promise.reject(
        new Error('Invalid image format. Expected: registry/repo:tag or registry/repo@sha256:digest')
      );
    }
    return Promise.resolve();
  };

  return (
    <Create
      saveButtonProps={{
        ...saveButtonProps,
        loading: submitting,
      }}
    >
      <Space direction="vertical" size="large" style={{ width: '100%' }}>
        <Alert
          message="Create New Sandbox"
          description="Configure and create an ephemeral test environment by forking existing Kubernetes resources."
          type="info"
          showIcon
          icon={<InfoCircleOutlined />}
        />

        <Form {...formProps} layout="vertical" onFinish={handleFinish}>
          {/* Basic Information */}
          <Card title="Basic Information" style={{ marginBottom: 24 }}>
            <Form.Item
              label="Sandbox Name"
              name="name"
              rules={[{ validator: validateSandboxName }]}
              extra="Must be lowercase alphanumeric with hyphens (e.g., pr-123)"
            >
              <Input placeholder="pr-123" />
            </Form.Item>

            <Form.Item
              label="Cluster"
              name="cluster"
              initialValue="staging-cluster"
              rules={[{ required: true, message: 'Please select a cluster' }]}
              extra="Target Kubernetes cluster where the sandbox will be created"
            >
              <Select>
                <Option value="staging-cluster">Staging Cluster</Option>
                <Option value="production-cluster">Production Cluster</Option>
              </Select>
            </Form.Item>

            <Form.Item
              label="TTL (Time To Live)"
              name="ttl_seconds"
              extra="Sandbox will be automatically deleted after this duration (optional)"
            >
              <InputNumber
                min={60}
                max={86400}
                placeholder="3600"
                style={{ width: '100%' }}
                addonAfter="seconds"
              />
            </Form.Item>
          </Card>

          {/* Labels */}
          <Card title="Labels" style={{ marginBottom: 24 }}>
            <Form.List name="labels">
              {(fields, { add, remove }) => (
                <>
                  {fields.map(({ key, name }) => (
                    <Space
                      key={key}
                      style={{ display: 'flex', marginBottom: 8 }}
                      align="baseline"
                    >
                      <Form.Item
                        name={[name, 'key']}
                        rules={[{ required: true, message: 'Key required' }]}
                        style={{ marginBottom: 0, width: 200 }}
                      >
                        <Input placeholder="github-pr" />
                      </Form.Item>
                      <Form.Item
                        name={[name, 'value']}
                        rules={[{ required: true, message: 'Value required' }]}
                        style={{ marginBottom: 0, width: 200 }}
                      >
                        <Input placeholder="123" />
                      </Form.Item>
                      <MinusCircleOutlined
                        onClick={() => remove(name)}
                        style={{ color: 'red', cursor: 'pointer' }}
                      />
                    </Space>
                  ))}
                  <Form.Item>
                    <Button type="dashed" onClick={() => add()} icon={<PlusOutlined />} block>
                      Add Label
                    </Button>
                  </Form.Item>
                </>
              )}
            </Form.List>
          </Card>

          {/* Forks */}
          <Card title="Forked Resources" style={{ marginBottom: 24 }}>
            <Alert
              message="At least one fork is required"
              description="Specify the Kubernetes resources you want to fork and customize."
              type="warning"
              showIcon
              style={{ marginBottom: 16 }}
            />

            <Form.List
              name="forks"
              rules={[
                {
                  validator: async (_, forks) => {
                    if (!forks || forks.length < 1) {
                      return Promise.reject(new Error('At least one fork is required'));
                    }
                  },
                },
              ]}
            >
              {(fields, { add, remove }, { errors }) => (
                <>
                  {fields.map(({ key, name }, index) => (
                    <Card
                      key={key}
                      type="inner"
                      title={`Fork ${index + 1}`}
                      extra={
                        <Button
                          type="link"
                          danger
                          onClick={() => remove(name)}
                          icon={<MinusCircleOutlined />}
                        >
                          Remove
                        </Button>
                      }
                      style={{ marginBottom: 16 }}
                    >
                      <Form.Item
                        name={[name, 'fork_of', 'kind']}
                        label="Resource Type"
                        rules={[{ required: true, message: 'Please select resource type' }]}
                      >
                        <Select placeholder="Select resource kind">
                          <Option value="Deployment">Deployment</Option>
                          <Option value="StatefulSet">StatefulSet</Option>
                        </Select>
                      </Form.Item>

                      <Form.Item
                        name={[name, 'fork_of', 'namespace']}
                        label="Namespace"
                        rules={[{ required: true, message: 'Please enter namespace' }]}
                      >
                        <Input placeholder="staging" />
                      </Form.Item>

                      <Form.Item
                        name={[name, 'fork_of', 'name']}
                        label="Baseline Resource Name"
                        rules={[{ required: true, message: 'Please enter resource name' }]}
                        extra="The existing resource to fork from"
                      >
                        <Input placeholder="payment-service-baseline" />
                      </Form.Item>

                      <Divider>Customizations</Divider>

                      <Form.Item label="Custom Images">
                        <Form.List name={[name, 'customizations', 'images']}>
                          {(imageFields, { add: addImage, remove: removeImage }) => (
                            <>
                              {imageFields.map((imageField) => (
                                <Space
                                  key={imageField.key}
                                  style={{ display: 'flex', marginBottom: 8 }}
                                  align="baseline"
                                >
                                  <Form.Item
                                    name={[imageField.name, 'image']}
                                    rules={[{ validator: validateImageURL }]}
                                    style={{ marginBottom: 0, width: 400 }}
                                  >
                                    <Input placeholder="registry.io/service:pr-123" />
                                  </Form.Item>
                                  <Form.Item
                                    name={[imageField.name, 'container_name']}
                                    style={{ marginBottom: 0, width: 150 }}
                                  >
                                    <Input placeholder="Container (optional)" />
                                  </Form.Item>
                                  <MinusCircleOutlined
                                    onClick={() => removeImage(imageField.name)}
                                    style={{ color: 'red', cursor: 'pointer' }}
                                  />
                                </Space>
                              ))}
                              <Button
                                type="dashed"
                                onClick={() => addImage()}
                                icon={<PlusOutlined />}
                                size="small"
                              >
                                Add Image Override
                              </Button>
                            </>
                          )}
                        </Form.List>
                      </Form.Item>

                      <Form.Item
                        name={[name, 'customizations', 'replicas']}
                        label="Replicas (Optional)"
                      >
                        <InputNumber min={1} max={10} placeholder="1" style={{ width: '100%' }} />
                      </Form.Item>

                      <Form.Item label="Environment Variables (Optional)">
                        <Form.List name={[name, 'customizations', 'env']}>
                          {(envFields, { add: addEnv, remove: removeEnv }) => (
                            <>
                              {envFields.map((envField) => (
                                <Space
                                  key={envField.key}
                                  style={{ display: 'flex', marginBottom: 8 }}
                                  align="baseline"
                                >
                                  <Form.Item
                                    name={[envField.name, 'name']}
                                    rules={[{ required: true, message: 'Name required' }]}
                                    style={{ marginBottom: 0, width: 200 }}
                                  >
                                    <Input placeholder="ENV_VAR" />
                                  </Form.Item>
                                  <Form.Item
                                    name={[envField.name, 'value']}
                                    rules={[{ required: true, message: 'Value required' }]}
                                    style={{ marginBottom: 0, width: 200 }}
                                  >
                                    <Input placeholder="value" />
                                  </Form.Item>
                                  <MinusCircleOutlined
                                    onClick={() => removeEnv(envField.name)}
                                    style={{ color: 'red', cursor: 'pointer' }}
                                  />
                                </Space>
                              ))}
                              <Button
                                type="dashed"
                                onClick={() => addEnv()}
                                icon={<PlusOutlined />}
                                size="small"
                              >
                                Add Environment Variable
                              </Button>
                            </>
                          )}
                        </Form.List>
                      </Form.Item>
                    </Card>
                  ))}

                  <Button
                    type="dashed"
                    onClick={() => add()}
                    icon={<PlusOutlined />}
                    block
                    size="large"
                  >
                    Add Fork
                  </Button>
                  <Form.ErrorList errors={errors} />
                </>
              )}
            </Form.List>
          </Card>
        </Form>
      </Space>
    </Create>
  );
};
